import { WebSectionSidebarLayout } from "@-/dev.improve.haniverse.routes.nav._components.sidebar.web-section";
import { usePathname } from "@-/com.npmjs.expo-router";
import type { ReactNode } from "react";
import { useSearchPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.search.navigator";
import { useBrowseCharactersPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.characters.navigator";
import { useBrowseLevelsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels.navigator";
import { useBrowsePrimitivesListPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.primitives.list.navigator";
import { useBrowseWordsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.words.navigator";
import { isWebBrowseSection } from "#web-section";

/** Every Browse tab is a URL prefix; the browse index redirects to Levels, so it counts as Levels. */
const BROWSE_TABS = [
  { key: "levels", label: "Levels", prefix: "/browse/levels" },
  { key: "characters", label: "Characters", prefix: "/browse/characters" },
  { key: "primitives", label: "Primitives", prefix: "/browse/primitives" },
  { key: "words", label: "Words", prefix: "/browse/words" },
  { key: "search", label: "Search", prefix: "/search" },
] as const;

function isActiveBrowseTab(pathname: string, prefix: string): boolean {
  if (pathname === "/browse") {
    return prefix === "/browse/levels";
  }
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Web's Browse sidebar: Levels, Characters, Primitives, Words and Search stacked beside every
 * Browse and Search page (native shows them as Browse's bottom tabs). Wraps the whole route slot
 * so the sidebar can appear on entering Browse without remounting the page beside it.
 */
export function WebBrowseSidebarLayout({
  children,
  hidden = false,
}: {
  children: ReactNode;
  /** A focused full-page screen hides the sidebar along with the header. */
  hidden?: boolean;
}) {
  const pathname = usePathname();
  const navigators = {
    characters: useBrowseCharactersPageNavigator(),
    levels: useBrowseLevelsPageNavigator(),
    primitives: useBrowsePrimitivesListPageNavigator(),
    search: useSearchPageNavigator(),
    words: useBrowseWordsPageNavigator(),
  };
  const items =
    !hidden && isWebBrowseSection(pathname)
      ? BROWSE_TABS.map((tab) => ({
          key: tab.key,
          label: tab.label,
          onPress: () => {
            navigators[tab.key].push.mutate();
          },
          selected: isActiveBrowseTab(pathname, tab.prefix),
          testID: `web-browse-sidebar-${tab.key}`,
        }))
      : null;

  return (
    <WebSectionSidebarLayout accessibilityLabel="Browse sections" items={items}>
      {children}
    </WebSectionSidebarLayout>
  );
}
